import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShare, faXmark } from '@fortawesome/free-solid-svg-icons';
import {
  faFacebook,
  faTwitter,
  faWhatsapp,
} from '@fortawesome/free-brands-svg-icons';
import './DailyVerse.css';
import { AuthContext } from '../../components/Authentication/AuthContext';
import { getEnglishBookName, getDeutschBookName } from '../Bible/BookNames';
import { getDailyVerseForToday, startDailyVerseCache } from './VerseCashe';

function DailyVerse() {
  const { language } = useContext(AuthContext);
  const [dailyVerse, setDailyVerse] = useState(null);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [loading, setLoading] = useState(true);

  const languageText = useMemo(() => {
    if (language === 'English') {
      return {
        title: 'Daily Verse',
        loading: 'Loading verse...',
        share: 'Share verse',
        close: 'Close',
      };
    }

    if (language === 'Deutsch') {
      return {
        title: 'Täglicher Vers',
        loading: 'Vers wird geladen...',
        share: 'Vers teilen',
        close: 'Schließen',
      };
    }

    return {
      title: 'இன்றைய வேத வசனம்',
      loading: 'வசனம் ஏற்றப்படுகிறது...',
      share: 'வசனத்தை பகிரவும்',
      close: 'மூடு',
    };
  }, [language]);

  useEffect(() => {
    let isMounted = true;

    const fetchDailyVerse = async () => {
      setLoading(true);

      try {
        startDailyVerseCache();

        const selectedVerse = await getDailyVerseForToday();

        if (!isMounted) {
          return;
        }

        setDailyVerse(selectedVerse);
      } catch (error) {
        console.error('Error fetching daily verse:', error);

        if (isMounted) {
          setDailyVerse(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDailyVerse();

    return () => {
      isMounted = false;
    };
  }, [language]);

  const getBookName = (bookName) => {
    if (language === 'English') {
      return getEnglishBookName(bookName);
    }

    if (language === 'Deutsch') {
      return getDeutschBookName(bookName);
    }

    return bookName;
  };

  const getVerseText = () => {
    if (!dailyVerse) {
      return '';
    }

    if (language === 'English') {
      return dailyVerse.english || '';
    }

    if (language === 'Deutsch') {
      return dailyVerse.deutsch || '';
    }

    return dailyVerse.tamil || '';
  };

  const getShareText = () => {
    if (!dailyVerse) {
      return '';
    }

    return `${getBookName(dailyVerse.book)} ${dailyVerse.chapter}:${
      dailyVerse.verse
    } - ${getVerseText()}`;
  };

  const toggleShareOptions = () => {
    setShowShareOptions((previousValue) => !previousValue);
  };

  const shareOnSocialMedia = (platform) => {
    if (!dailyVerse) {
      return;
    }

    const text = getShareText();
    let shareUrl = '';

    if (platform === 'facebook') {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        window.location.href
      )}&quote=${encodeURIComponent(text)}`;
    }

    if (platform === 'twitter') {
      shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}`;
    }

    if (platform === 'whatsapp') {
      shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
        text
      )}`;
    }

    if (shareUrl) {
      const shareWindow = window.open(shareUrl, '_blank', 'noopener,noreferrer');

      if (shareWindow) {
        shareWindow.focus();
      }
    }
  };

  return (
    <div className="daily-verse-container">
      <article className="daily-verse-card">
        <div className="daily-verse-header">
          <h3 className="daily-verse-title">🙏 {languageText.title}</h3>

          {dailyVerse && (
            <button
              type="button"
              className="daily-verse-share-main"
              onClick={toggleShareOptions}
              aria-label={languageText.share}
            >
              <FontAwesomeIcon icon={showShareOptions ? faXmark : faShare} />
            </button>
          )}
        </div>

        {loading ? (
          <div className="daily-verse-loading">
            <div className="daily-verse-spinner"></div>
            <p>{languageText.loading}</p>
          </div>
        ) : dailyVerse ? (
          <div className="daily-verse-body">
            <div className="daily-verse-reference">
              <span className="daily-verse-book">
                {getBookName(dailyVerse.book)}
              </span>
              <span>{dailyVerse.chapter}</span>
              <span>:</span>
              <span>{dailyVerse.verse}</span>
            </div>

            <p className="daily-verse-text">{getVerseText()}</p>
          </div>
        ) : (
          <div className="daily-verse-loading">
            <p>{languageText.loading}</p>
          </div>
        )}

        {showShareOptions && dailyVerse && (
          <div className="daily-verse-share-options">
            <button
              type="button"
              onClick={() => shareOnSocialMedia('facebook')}
              aria-label="Facebook"
            >
              <FontAwesomeIcon icon={faFacebook} />
            </button>

            <button
              type="button"
              onClick={() => shareOnSocialMedia('twitter')}
              aria-label="Twitter"
            >
              <FontAwesomeIcon icon={faTwitter} />
            </button>

            <button
              type="button"
              onClick={() => shareOnSocialMedia('whatsapp')}
              aria-label="WhatsApp"
            >
              <FontAwesomeIcon icon={faWhatsapp} />
            </button>
          </div>
        )}
      </article>
    </div>
  );
}

export default DailyVerse;